import { useEffect, useMemo } from "react"
import { useInfiniteQuery } from "react-query"

export const defaultHandleScroll = ({
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
}) => {
  return (e) => {
    const target = e.target.scrollingElement || e.target
    const { scrollHeight, scrollTop, clientHeight } = target
    const scrolledToBottom = scrollHeight - scrollTop - 200 <= clientHeight
    if (scrolledToBottom) {
      if (hasNextPage && !isFetchingNextPage) fetchNextPage()
    }
  }
}

const useFullPageInfiniteScroll = ({
  queryKey,
  queryFn,
  scrollElement = null,
  handleScroll = defaultHandleScroll,
  refetchInterval = false,
  initialData = null,
}) => {
  const {
    isFetching,
    isError,
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    queryKey,
    queryFn,
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.length) {
        return pages.length + 1
      }
    },
    refetchInterval: refetchInterval,
    refetchOnWindowFocus: false,
    initialData: () => {
      if (initialData) {
        return {
          pages: [initialData],
          pageParams: [null, 1],
        }
      }
    },
  })

  useEffect(() => {
    const handleScrollFn = handleScroll({
      hasNextPage,
      fetchNextPage,
      isFetchingNextPage,
    })
    const element = scrollElement?.current || document
    const eventTarget = element === document ? window : element

    eventTarget.addEventListener("scroll", handleScrollFn)
    return () => {
      eventTarget.removeEventListener("scroll", handleScrollFn)
    }
  }, [
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    scrollElement,
    handleScroll,
  ])

  const items = useMemo(() => data?.pages?.flat?.(), [data])

  return {
    items,
    isFetching,
    isError,
    isLoading,
    hasNextPage,
    fetchNextPage,
    error,
  }
}

export default useFullPageInfiniteScroll
